import { inject, Injectable } from '@angular/core';
import { SessionStorageService } from '../../../core/services/session-storage/session-storage.service';
import { environment } from '../../../../environments/environment';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { campaignlist, CampaignTemplate, listTemplate, TemplateSummary } from '../campaign-model/campaign-model';
import { CampaignPayload } from '../create-campaign-model/create-campaign-model';
import { ScheduleCampaignPayload, SendCampaignPayload, ViewRecipientsPayload, cancelCampaignPayload } from '../schedule-campaign-model/schedule-campaign';
import { ViewCampaignpayload, dropdownpayload } from '../view-campaign-model/view-campaign-model';
import { TemplateMediaUploadResponse, TemplatePayload } from '../create-template/create-template-model/create-template-model';

@Injectable({
  providedIn: 'root'
})
export class CampaignService {
  campaignTemplates: CampaignTemplate[] = [];
  isLoading: boolean = true;
  private sessionStorageService = inject(SessionStorageService)

  constructor() { }
  async listTemplateSubDropdown(payload: dropdownpayload): Promise<any[]> {

    const API_URL: string =
      environment.baseUrl + environment.apis.viewtemplate;


    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'POST',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization':
          this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: payload
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return httpResponse.data.responsedata || [];
      })
      .catch((error) => {
        console.error('Full Error:', error);
        console.error('Error Response:', error.response);
        console.error('Error Data:', error.response?.data);

        throw error;
      });
  }

  async viewcampaign(payload: ViewCampaignpayload): Promise<CampaignTemplate[]> {

    const API_URL: string = environment.baseUrl + environment.apis.viewcampaign;
    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'POST',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: payload
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        // 1. We open the box and grab the messy array the backend sent us
        const rawArray = httpResponse.data.responsedata;

        // 2. We use .map() to translate every item in that array one by one
        const cleanArray: CampaignTemplate[] = rawArray.map((rawItem: any) => {
          return {
            id: rawItem.templateid,               // translate templateid -> id
            name: rawItem.campaignname,           // translate campaignname -> name
            subject: rawItem.campaignsubject,     // translate campaignsubject -> subject
            htmlContent: rawItem.campaigntemplate // translate campaigntemplate -> htmlContent
          };
        });

        // We hand the translated, clean array back to the Component
        return cleanArray;
      })
      //  We catch any errors safely
      .catch((error) => {
        // Check if the server actually responded (e.g., 401 Unauthorized, 404 Not Found)
        if (error.response) {
          throw new AxiosError(
            error.response.data?.message || 'API Error', // The message
            error.response.status.toString()             // The status code
          );
        }
        // If the server didn't respond at all (Server offline, Network failure, CORS)
        else {
          throw new AxiosError('Network Error: Could not reach the server.', '500');
        }
      });
  }

  async createCampaign(newCampaign: CampaignPayload) {
    const API_URL: string = environment.baseUrl + environment.apis.createcampaign;

    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'POST',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: newCampaign.toString() // Triggers the toString method we just built
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return JSON.stringify(httpResponse);
      })
      .catch((error) => {
        throw new AxiosError(
          error.response?.data?.message || 'API Error',
          error.response?.status?.toString()
        );
      });
  }
  //createtemplate postapi
  async createTemplate(templatePayload: TemplatePayload) {
    const API_URL: string = environment.baseUrl + environment.apis.createtemplate;

    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'POST',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: templatePayload.toString()
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return JSON.stringify({
          status: httpResponse.status,
          data: httpResponse.data
        });
      })
      .catch((error) => {
        throw new AxiosError(
          error.response?.data?.message || 'API Error',
          error.response?.status?.toString()
        );
      });
  }

  //template dashboard getapi
  async listTemplates(): Promise<TemplateSummary[]> {
    const API_URL: string = environment.baseUrl + environment.apis.listtemplate;

    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'POST',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: { id: parseInt(this.sessionStorageService.getCurrentUserId(), 10) }
      // data: TemplatePayload
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        const rawArray = httpResponse.data.responsedata || [];

        // We use .map() to translate every item in that array one by one
        const cleanArray: TemplateSummary[] = rawArray.map((rawItem: any) => {
          return {
            id: rawItem.id,
            name: rawItem.name,
            subject: rawItem.subject,
            channel: rawItem.channel,
            startDate: rawItem.start_date ?? rawItem.startDate,
            endDate: rawItem.end_date ?? rawItem.endDate,
            status: rawItem.status
          };
        });

        return cleanArray;   // return the mapped array, not the raw one
        // Raw array passed straight through — adjust mapping here once the real
        // response shape is known (similar to viewcampaign()'s field translation,
        // if the backend's field names differ from TemplateSummary's).

        return httpResponse.data.responsedata || [];
      })

      .catch((error) => {
        if (error.response) {
          throw new AxiosError(
            error.response.data?.message || 'API Error',
            error.response.status.toString()
          );
        } else {
          throw new AxiosError('Network Error: Could not reach the server.', '500');
        }
      });
  }

  async getTemplate(id: number): Promise<any> {
  const API_URL: string = environment.baseUrl + environment.apis.gettemplate;

  const REQUEST_CONFIG: AxiosRequestConfig = {
    method: 'POST',
    maxBodyLength: Infinity,
    url: API_URL,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
    },
    data: { id }
  };

  return await axios.request(REQUEST_CONFIG)
    .then((httpResponse) => {
      return httpResponse.data.responsedata ?? httpResponse.data;
    })
    .catch((error) => {
      if (error.response) {
        throw new AxiosError(
          error.response.data?.message || 'API Error',
          error.response.status.toString()
        );
      } else {
        throw new AxiosError('Network Error: Could not reach the server.', '500');
      }
    });
}

  

  //dropdown for camapign list
  async campaignlist(channelName: string = 'email'): Promise<campaignlist[]> {

    const API_URL: string = environment.baseUrl + environment.apis.campaignlist;
    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'POST',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      // data: {
      //   "piasRepCode": this.sessionStorageService.getCurrentRepCode().toString()
      // }
      data: { "channelName": channelName }
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        // We just pass the raw array straight through!
        const listArray = httpResponse.data.responsedata as campaignlist[];
        return listArray;
      })
      //  We catch any errors safely
      .catch((error) => {
        // Check if the server actually responded (e.g., 401 Unauthorized, 404 Not Found)
        if (error.response) {
          throw new AxiosError(
            error.response.data?.message || 'API Error', // The message
            error.response.status.toString()             // The status code
          );
        }
        // If the server didn't respond at all (Server offline, Network failure, CORS)
        else {
          throw new AxiosError('Network Error: Could not reach the server.', '500');
        }
      });
  }



  async listcampaign(): Promise<listTemplate[]> {

    const API_URL: string = environment.baseUrl + environment.apis.listcampaign
    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'GET',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      // data: {
      //   "piasRepCode": this.sessionStorageService.getCurrentRepCode().toString()
      // }
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        // We just pass the raw array straight through!
        const listArray = httpResponse.data.responsedata;
        return listArray;
      })
      //  We catch any errors safely
      .catch((error) => {
        // Check if the server actually responded (e.g., 401 Unauthorized, 404 Not Found)
        if (error.response) {
          throw new AxiosError(
            error.response.data?.message || 'API Error', // The message
            error.response.status.toString()             // The status code
          );
        }
        // If the server didn't respond at all (Server offline, Network failure, CORS)
        else {
          throw new AxiosError('Network Error: Could not reach the server.', '500');
        }
      });
  }

  async scheduleCampaign(scheduleCampaign: ScheduleCampaignPayload) {
    // NOTE: Update 'environment.apis.scheduleCampaign' to match your actual backend URL
    const API_URL: string = environment.baseUrl + environment.apis.schedulecampaign;

    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'POST', // Changed from GET to POST
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        // Make sure your Bearer token logic is correct here
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: scheduleCampaign // This is where we attach the JSON object!
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return httpResponse.data;
      })
      .catch((error) => {
        if (error.response) {
          throw new AxiosError(
            error.response.data?.message || 'Error scheduling campaign',
            error.response.status.toString()
          );
        } else {
          throw new AxiosError('Network Error: Could not reach the server.', '500');
        }
      });
  }
  async cancelCampaign(cancelPayload: cancelCampaignPayload) {
    // NOTE: Update 'environment.apis.scheduleCampaign' to match your actual backend URL
    const API_URL: string = environment.baseUrl + environment.apis.schedulecampaign;

    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'POST', // Changed from GET to POST
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        // Make sure your Bearer token logic is correct here
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: cancelPayload // This is where we attach the JSON object!
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return httpResponse.data;
      })
      .catch((error) => {
        if (error.response) {
          throw new AxiosError(
            error.response.data?.message || 'Error scheduling campaign',
            error.response.status.toString()
          );
        } else {
          throw new AxiosError('Network Error: Could not reach the server.', '500');
        }
      });
  }

  async sendCampaign(sendCampaign: SendCampaignPayload) {
    // NOTE: Update 'environment.apis.sendCampaign' to match your actual backend URL
    const API_URL: string = environment.baseUrl + environment.apis.sendcampaign;

    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'POST', // Changed from GET to POST
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        // Make sure your Bearer token logic is correct here
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: sendCampaign  // This is where we attach the JSON object!
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return httpResponse.data;
      })
      .catch((error) => {
        if (error.response) {
          throw new AxiosError(
            error.response.data?.message || 'Error scheduling campaign',
            error.response.status.toString()
          );
        } else {
          throw new AxiosError('Network Error: Could not reach the server.', '500');
        }
      });
  }

  async viewRecipients(viewRecipients: ViewRecipientsPayload) {
    // NOTE: Update 'environment.apis.viewRecipients' to match your actual backend URL
    const API_URL: string = environment.baseUrl + environment.apis.viewrecipients;

    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'POST', // Changed from GET to POST
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        // Make sure your Bearer token logic is correct here
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: viewRecipients  // This is where we attach the JSON object!
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return httpResponse.data;
      })
      .catch((error) => {
        if (error.response) {
          throw new AxiosError(
            error.response.data?.message || 'Error scheduling campaign',
            error.response.status.toString()
          );
        } else {
          throw new AxiosError('Network Error: Could not reach the server.', '500');
        }
      });
  }


  async uploadTemplateMedia(templateId: number, file: File): Promise<TemplateMediaUploadResponse> {
  const API_URL: string = environment.baseUrl + environment.apis.uploadtemplatemedia;

  const formData = new FormData();
  formData.append('templateId', templateId.toString());
  formData.append('file', file, file.name);

  const REQUEST_CONFIG: AxiosRequestConfig = {
    method: 'POST',
    maxBodyLength: Infinity,
    url: API_URL,
    headers: {
      // Content-Type kudukkadheenga, browser boundary-oda set pannum
      'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
    },
    data: formData
  };

  return await axios.request(REQUEST_CONFIG)
    .then((httpResponse) => {
      const res = httpResponse.data.responsedata;
      return { id: res.id, s3Url: res.s3Url } as TemplateMediaUploadResponse;
    })
    .catch((error) => {
      if (error.response) {
        throw new AxiosError(
          error.response.data?.message || 'Error uploading file',
          error.response.status.toString()
        );
      } else {
        throw new AxiosError('Network Error: Could not reach the server.', '500');
      }
    });
}
  async deleteTemplate(id: number): Promise<any> {
    const API_URL: string = environment.baseUrl + environment.apis.deletetemplate;

    const REQUEST_CONFIG: AxiosRequestConfig = {
      method: 'POST',
      maxBodyLength: Infinity,
      url: API_URL,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
      },
      data: { id }
    };

    return await axios.request(REQUEST_CONFIG)
      .then((httpResponse) => {
        return JSON.stringify({
          status: httpResponse.status,
          data: httpResponse.data
        });
      })
      .catch((error) => {
        if (error.response) {
          throw new AxiosError(
            error.response.data?.message || error.response.data?.data?.message || 'Error deleting template',
            error.response.status.toString()
          );
        } else {
          throw new AxiosError('Network Error: Could not reach the server.', '500');
        }
      });
  }


}
